import { useLayoutEffect, useState } from "react";

import { useThrottledCallback } from "@nens/shared-utils";

type PositionedRectResult = Omit<DOMRect, "toJSON">;
type SizeRectResult = Pick<PositionedRectResult, "height" | "width">;

export type RectResult<TrackPosition extends boolean> = TrackPosition extends true
  ? PositionedRectResult
  : SizeRectResult;

const POSITIONED_EMPTY_RECT: PositionedRectResult = {
  height: 0,
  width: 0,
  top: 0,
  bottom: 0,
  left: 0,
  right: 0,
  x: 0,
  y: 0,
};

const SIZE_EMPTY_RECT: SizeRectResult = {
  height: 0,
  width: 0,
};

function rectsAreEqual(a: PositionedRectResult, b: PositionedRectResult): boolean;
function rectsAreEqual(a: SizeRectResult, b: SizeRectResult): boolean;
function rectsAreEqual(
  a: PositionedRectResult | SizeRectResult,
  b: PositionedRectResult | SizeRectResult
) {
  const isTheSameSize = a.height === b.height && a.width === b.width;

  // Size rect
  if (!("top" in a) || !("top" in b)) {
    return isTheSameSize;
  }

  // Positioned rect
  return (
    isTheSameSize &&
    a.top === b.top &&
    a.bottom === b.bottom &&
    a.left === b.left &&
    a.right === b.right &&
    a.x === b.x &&
    a.y === b.y
  );
}

function getRect<T extends HTMLElement, TrackPosition extends boolean>(
  element: T | null | undefined,
  trackPosition: TrackPosition
): RectResult<TrackPosition>;
function getRect<T extends HTMLElement>(
  element: T | null | undefined,
  trackPosition: boolean
): PositionedRectResult | SizeRectResult {
  if (!element) {
    if (trackPosition) {
      return POSITIONED_EMPTY_RECT;
    }
    return SIZE_EMPTY_RECT;
  }

  const { height, width, top, bottom, left, right, x, y } = element.getBoundingClientRect();
  if (trackPosition) {
    return { height, width, top, bottom, left, right, x, y };
  }
  return { height, width };
}

// Gets first ancestor with a position other than "static" (or the documentElement if none found).
function getPositionedAncestor(element: HTMLElement): HTMLElement | null {
  let parent = element.parentElement;
  while (parent && parent !== document.documentElement) {
    const position = getComputedStyle(parent).position;
    if (
      position === "relative" ||
      position === "absolute" ||
      position === "fixed" ||
      position === "sticky"
    ) {
      return parent;
    }
    parent = parent.parentElement;
  }
  return null;
}

/**
 * Returns the bounding rect of a DOM element ref.
 *
 * @param trackPosition - When true, also tracks position properties (left/top/x/y/right/bottom) by
 * observing siblings, parent, the positioned ancestor, and listening to scroll/resize.
 * Only pass true when you actually use left/top/x/y/right/bottom from the result.
 */
export function useRect<T extends HTMLElement, TrackPosition extends boolean = false>(
  ref: React.RefObject<T>,
  trackPosition: TrackPosition = false as TrackPosition
): RectResult<TrackPosition> {
  const [rect, setRect] = useState<RectResult<TrackPosition>>(() =>
    getRect(ref.current, trackPosition)
  );

  const update = useThrottledCallback(() => {
    const newRect = getRect(ref.current, trackPosition);
    setRect((prevRect) => (rectsAreEqual(newRect, prevRect) ? prevRect : newRect));
  }, 100);

  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;

    update();

    const resizeObserver = new ResizeObserver(update);

    // Observe the element itself for size changes.
    resizeObserver.observe(element);

    if (trackPosition) {
      // Parent: catches padding/border changes that shift children.
      if (element.parentElement) {
        resizeObserver.observe(element.parentElement);
      }

      // Positioned ancestor (if different from parent): for absolute elements, this is
      // the containing block whose padding/border directly affects child positions.
      const positionedAncestor = getPositionedAncestor(element);
      if (positionedAncestor && positionedAncestor !== element.parentElement) {
        resizeObserver.observe(positionedAncestor);
      }

      // Siblings: in flex/grid any sibling resizing can shift this element's position
      // even if the element itself and its parent don't change size.
      element.parentElement?.children &&
        Array.from(element.parentElement.children)
          .filter((child) => child !== element)
          .forEach((sibling) => resizeObserver.observe(sibling as HTMLElement));

      window.addEventListener("resize", update, { passive: true });
      window.addEventListener("scroll", update, { passive: true, capture: true });
    }

    return () => {
      resizeObserver.disconnect();
      if (trackPosition) {
        window.removeEventListener("resize", update);
        window.removeEventListener("scroll", update, { capture: true });
      }
    };
    // Update when the ref changes seeing that the element may have changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref.current, update, trackPosition]);

  return rect;
}
